import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { ContactCheckboxes, useTriSelection, type EntityContact, EMPTY_CONTACT, type TriValues } from './ContactCheckboxes'

interface EmailFormProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  contact?: EntityContact
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Send Mail action (CRMService/SendEmail). */
export function EmailForm({ entityId, entityType = 'lead', contact = EMPTY_CONTACT, onSuccess }: EmailFormProps) {
  const [templates, setTemplates] = useState<Option[]>([])
  const [fromMailIds, setFromMailIds] = useState<Option[]>([])

  const [fromMailId, setFromMailId] = useState<string>('')
  const [replyTo, setReplyTo] = useState<string>('')
  const [templateId, setTemplateId] = useState<string>('')
  const [subject, setSubject] = useState<string>('')
  const [body, setBody] = useState<string>('')

  const emailValues: TriValues = [contact.email, contact.email1, contact.email2]
  const emails = useTriSelection(emailValues)

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const [tpls, froms] = await Promise.all([api.fetchEmailTemplates(), api.fetchFromMailIds()])
        if (!active) return
        setTemplates(tpls)
        setFromMailIds(froms)
        if (froms.length > 0) {
          setFromMailId(froms[0].id)
          setReplyTo(froms[0].id)
        }
      } catch {
        if (active) setError('Failed to load Email options.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  /** Template pick autofills the subject (still editable), like the legacy form. */
  const handleTemplateChange = async (value: string) => {
    setTemplateId(value)
    if (!value) return
    try {
      const tplSubject = await api.fetchMailTemplateSubject(value)
      if (tplSubject) setSubject(tplSubject)
    } catch {
      // Subject autofill is convenience only — the user can still type one.
    }
  }

  const handleReset = () => {
    if (fromMailIds.length > 0) {
      setFromMailId(fromMailIds[0].id)
      setReplyTo(fromMailIds[0].id)
    } else {
      setFromMailId('')
      setReplyTo('')
    }
    setTemplateId('')
    setSubject('')
    setBody('')
    emails.reset()
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emails.anySelected) {
      setError('Please select at least one email address.')
      return
    }
    if (!subject.trim()) {
      setError('Subject is required.')
      return
    }
    if (!body.trim()) {
      setError('Email body is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitEmail({
        entityId,
        entityType,
        email: emails.selected[0],
        email1: emails.selected[1],
        email2: emails.selected[2],
        templateId,
        subject,
        body,
        emailType: templateId === '' ? 'Compose' : templateId,
        fromMailId,
        replyTo,
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to send Email.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading Email options…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="em-from">
          From
        </label>
        <select
          id="em-from"
          className="form-control"
          value={fromMailId}
          onChange={(e) => setFromMailId(e.target.value)}
        >
          <option value="">-- Select From --</option>
          {fromMailIds.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="em-replyto">
          Reply To
        </label>
        <select
          id="em-replyto"
          className="form-control"
          value={replyTo}
          onChange={(e) => setReplyTo(e.target.value)}
        >
          <option value="">-- Select Reply To --</option>
          {fromMailIds.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="em-template">
          Email Template
        </label>
        <select
          id="em-template"
          className="form-control"
          value={templateId}
          onChange={(e) => void handleTemplateChange(e.target.value)}
        >
          <option value="">Compose</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <ContactCheckboxes
        label="Email Address(es)"
        idPrefix="em-email"
        values={emailValues}
        checked={emails.checked}
        onToggle={emails.toggle}
        emptyMessage="This record has no email address."
      />

      <div className="form-group">
        <label className="form-label" htmlFor="em-subject">
          Subject <span className="req">*</span>
        </label>
        <input
          id="em-subject"
          type="text"
          className="form-control"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email Subject"
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="em-body">
          Email Body <span className="req">*</span>
        </label>
        <textarea
          id="em-body"
          rows={5}
          className="form-control"
          placeholder="Write your email body here..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Sending…' : 'Send Mail'}
        </button>
      </div>
    </div>
  )
}
